// Selector composition helpers for class-variable transform pipeline.
// Example: ".btn &var(--hoverRule)" -> ".btn:hover".
import { parseClassVariableRule, combineSelector } from '../parser'
import { resolveStructuredVarReference } from './reference-values'
import { readAdjacentVarReferenceAt } from './adjacent-var'
import { isWhitespace, startsWithWhitespace } from './scanner-chars'
import type { ClassTransformOptions } from './types'

// Resolve selector var references using class/object/plain variable maps.
export function resolveSelectorReferences(selector: string, options: ClassTransformOptions): string {
  let output = ''
  let index = 0

  // Scan selector and replace `var(...)` tokens when values can be resolved.
  while (index < selector.length) {
    const token = readAdjacentVarReferenceAt(selector, index)
    // Keep raw character when no var reference starts at this index.
    if (!token) {
      output += selector[index]
      index += 1
      continue
    } else {
      // Continue resolving parsed selector var token.
    }

    const reference = token.reference
    // Resolve object and array-item access references first.
    if (reference.objectKey || reference.arrayIndex !== null) {
      const resolved = resolveStructuredVarReference(reference, options)
      output += resolved ?? token.raw
      index = reference.end
      continue
    } else {
      // Continue plain/class selector reference resolution.
    }

    const classValue = options.classVariables[reference.variableName]
    // Replace class variable with its selector part when possible.
    if (classValue) {
      const classRule = parseClassVariableRule(classValue)
      if (classRule) {
        output = token.hasAmpersandPrefix
          ? appendAdjacentSelector(output, classRule.selector.trim())
          : `${output}${classRule.selector.trim()}`
        index = reference.end
        continue
      } else {
        // Keep resolving using plain variable fallback.
      }
    } else {
      // Continue plain variable lookup when no class variable exists.
    }

    const plainValue = options.variables[reference.variableName]
    // Replace plain variable with literal selector text when available.
    if (plainValue) {
      output += plainValue.trim()
      index = reference.end
      continue
    } else {
      // Keep unresolved var token as-is.
      output += reference.raw
      index = reference.end
    }
  }

  // Return selector text with resolved var references.
  return output
}

// Append one exact `&var(...)` selector token without leaving a literal ampersand.
function appendAdjacentSelector(current: string, tokenSelector: string): string {
  const normalizedCurrent = current.trim()
  // Use token selector directly when no base selector exists yet.
  if (!normalizedCurrent) {
    return tokenSelector
  } else {
    // Continue adjacent selector composition for non-empty base.
  }

  // Return selector composition using the same merge rules as class composition.
  return combineSelector(normalizedCurrent, tokenSelector)
}

// Append selector text while preserving whitespace-driven descendant semantics.
export function appendSelectorText(current: string, fragment: string): string {
  const normalizedCurrent = current.trim()
  const normalizedFragment = fragment.trim()
  // Skip empty fragments so selector output stays stable.
  if (!normalizedFragment) {
    return normalizedCurrent
  } else {
    // Continue selector concatenation for non-empty fragment.
  }

  // Use fragment as first selector segment when current is empty.
  if (!normalizedCurrent) {
    return normalizedFragment
  } else {
    // Continue merge behavior for existing selector prefix.
  }

  // Preserve descendant merge when incoming fragment starts with whitespace.
  if (startsWithWhitespace(fragment)) {
    return `${normalizedCurrent} ${normalizedFragment}`
  } else {
    // Return compact concatenation without descendant space.
    return `${normalizedCurrent}${normalizedFragment}`
  }
}

// Merge one class-token selector using whitespace-aware join behavior.
export function appendTokenSelector(current: string, tokenSelector: string, previousChar: string): string {
  const normalizedCurrent = current.trim()
  // Use token selector directly when this is the first selector segment.
  if (!normalizedCurrent) {
    return tokenSelector
  } else {
    // Continue merge behavior for non-empty selector base.
  }

  // Keep descendant relation when token follows whitespace in source text.
  if (isWhitespace(previousChar)) {
    return `${normalizedCurrent} ${tokenSelector}`
  } else {
    // Return combined selector when token is adjacent to base.
    return combineSelector(normalizedCurrent, tokenSelector)
  }
}
